import { Injectable, UnauthorizedException } from '@nestjs/common';
import { PrismaService } from '@cart/api-data-access';
import { createHash, randomBytes } from 'crypto';

/**
 * Revocable login sessions backed by rotating refresh tokens.
 *
 * Replaces the previous model, where the only credential was a 1-hour JWT with
 * no server-side record: logout could not invalidate anything, a password
 * change left other sessions running, and a stolen token was usable until it
 * expired with no way to kill it.
 *
 * The design that makes a 30-day window defensible:
 *
 *  - the refresh token is long-lived but ROTATES on every use
 *  - every token rotated from one login shares a `familyId`
 *  - presenting a refresh token twice can only mean it was captured, because
 *    the legitimate client already swapped its copy for a new one. That
 *    revokes the entire family — ending the attacker's access and the
 *    victim's, which is correct: it forces a re-login rather than leaving both
 *    sessions alive.
 *
 * The access token stays a short-lived JWT and is unchanged here; this service
 * governs what can mint a new one.
 */
@Injectable()
export class SessionService {
  /**
   * Long, because rotation + reuse detection bounds the real exposure.
   *
   * Expressed in MINUTES rather than days purely so a test can set it to 3 and
   * watch a session genuinely end — "log out after the refresh token dies" is
   * otherwise a 30-day wait and therefore never verified. Default is 30 days.
   */
  private readonly REFRESH_TTL_MINUTES = Number(process.env.AUTH_REFRESH_TTL_MINUTES ?? 30 * 24 * 60);

  constructor(private readonly prisma: PrismaService) {}

  /**
   * 256 bits from a CSPRNG. `randomBytes`, never `Math.random()` — this value
   * is the credential.
   */
  private mintToken(): string {
    return randomBytes(32).toString('hex');
  }

  /**
   * Only the hash is stored, so a database dump does not hand over usable
   * sessions. SHA-256 rather than bcrypt is deliberate: this is a
   * high-entropy random token, not a human password, so there is nothing to
   * brute-force and refresh happens on a hot path.
   */
  private hash(token: string): string {
    return createHash('sha256').update(token).digest('hex');
  }

  private expiry(): Date {
    return new Date(Date.now() + this.REFRESH_TTL_MINUTES * 60 * 1000);
  }

  /** Starts a new session family — called on login/register. */
  async issue(userId: string, context?: { userAgent?: string; ipAddress?: string }): Promise<string> {
    const token = this.mintToken();
    const familyId = randomBytes(16).toString('hex');

    await this.prisma.userSession.create({
      data: {
        userId,
        familyId,
        tokenHash: this.hash(token),
        userAgent: context?.userAgent?.slice(0, 400) ?? null,
        ipAddress: context?.ipAddress ?? null,
        expiresAt: this.expiry(),
      },
    });

    return token;
  }

  /**
   * Validates a refresh token and rotates it, returning the userId and the
   * replacement token.
   *
   * Throws on anything suspicious rather than degrading: an unknown, expired
   * or already-revoked token is not a recoverable state.
   */
  async rotate(
    token: string,
    context?: { userAgent?: string; ipAddress?: string },
  ): Promise<{ userId: string; refreshToken: string }> {
    const tokenHash = this.hash(token);
    const session = await this.prisma.userSession.findUnique({ where: { tokenHash } });

    if (!session) throw new UnauthorizedException('Invalid session');

    // REUSE DETECTION. A revoked token being presented means someone kept a
    // copy: the legitimate client rotated away from it, so only a captured
    // copy can still be in circulation. Kill the whole family.
    if (session.revokedAt) {
      await this.revokeFamily(session.familyId, 'refresh-token-reuse-detected');
      throw new UnauthorizedException('Session revoked');
    }

    if (session.expiresAt.getTime() <= Date.now()) {
      throw new UnauthorizedException('Session expired');
    }

    const replacement = this.mintToken();

    // One transaction: the old token must not be usable the instant the new
    // one exists, or a crash between the two writes leaves either two live
    // tokens or none.
    await this.prisma.$transaction([
      this.prisma.userSession.update({
        where: { id: session.id },
        data: { revokedAt: new Date(), revokedReason: 'rotated', lastUsedAt: new Date() },
      }),
      this.prisma.userSession.create({
        data: {
          userId: session.userId,
          familyId: session.familyId, // same family: this is the same login
          tokenHash: this.hash(replacement),
          userAgent: context?.userAgent?.slice(0, 400) ?? session.userAgent,
          ipAddress: context?.ipAddress ?? session.ipAddress,
          expiresAt: this.expiry(),
        },
      }),
    ]);

    return { userId: session.userId, refreshToken: replacement };
  }

  /** Ends one session — the logout path. */
  async revoke(token: string, reason = 'logout'): Promise<void> {
    await this.prisma.userSession.updateMany({
      where: { tokenHash: this.hash(token), revokedAt: null },
      data: { revokedAt: new Date(), revokedReason: reason },
    });
  }

  async revokeFamily(familyId: string, reason: string): Promise<void> {
    await this.prisma.userSession.updateMany({
      where: { familyId, revokedAt: null },
      data: { revokedAt: new Date(), revokedReason: reason },
    });
  }

  /**
   * Ends every session for a user. This is what a password change, an email
   * change or an account suspension must call — none of which could end other
   * sessions before.
   */
  async revokeAllForUser(userId: string, reason: string): Promise<number> {
    const result = await this.prisma.userSession.updateMany({
      where: { userId, revokedAt: null },
      data: { revokedAt: new Date(), revokedReason: reason },
    });
    return result.count;
  }

  /**
   * Deletes sessions that are long dead. Without this the table only ever
   * grows: rotation writes a NEW row on every refresh and revokes the old one
   * rather than deleting it, so rows accumulate per REFRESH, not per login.
   * With a 15-minute access token, one user active eight hours a day produces
   * roughly 30 rows a day — about 900 per active user at 30-day retention,
   * multiplied by devices.
   *
   * The grace period is why this deletes on `revokedAt`/`expiresAt` plus a
   * delay rather than immediately: a revoked session is EVIDENCE. If reuse
   * detection fires, those rows show when a token was stolen and from which
   * IP, so they need to outlive the incident rather than vanish the moment
   * they stop being usable.
   *
   * @returns how many rows were removed
   */
  async prune(graceDays = SessionService.PRUNE_GRACE_DAYS): Promise<number> {
    const cutoff = new Date(Date.now() - graceDays * 24 * 60 * 60 * 1000);

    const result = await this.prisma.userSession.deleteMany({
      where: {
        OR: [
          { expiresAt: { lt: cutoff } },
          { revokedAt: { lt: cutoff } },
        ],
      },
    });

    return result.count;
  }

  /** Overridable via env so retention can be tuned without a deploy. */
  private static readonly PRUNE_GRACE_DAYS = Number(process.env.SESSION_PRUNE_GRACE_DAYS ?? 7);

  /** Backs the "active sessions" UI. */
  async listActive(userId: string) {
    return this.prisma.userSession.findMany({
      where: { userId, revokedAt: null, expiresAt: { gt: new Date() } },
      select: { id: true, userAgent: true, ipAddress: true, lastUsedAt: true, createdAt: true, expiresAt: true },
      orderBy: { lastUsedAt: 'desc' },
    });
  }
}
